import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DownloadCsv, FullTable, ManageActions, SelectedFilters, SimpleTooltip } from 'components';
import { clientActions, fundingSourceActions, httpRequestsOnSuccessActions, tagActions } from 'store';
import {
    clientStatusEnums,
    FindSuccess,
    Images,
    PermissionList,
    RolePermission,
    SavePage,
    sortListByKey,
    tagEnums,
    useModal,
} from 'utils';
import { ClientIntakeForm, CreateClient } from 'fragments';
import {
    ACTION_TYPE,
    clientHead,
    clientBody,
    clientNotYet,
    clientStatusFilterList,
    defaultStatuses,
    clientExpirationEnums,
    canReadClientPayers,
    canFilterClientsByPayer,
} from './constants';
import { buttonsStyle } from '../../components/buttons/styles';
import { ResendIntakeForm, ClientConfirmForm } from './fragments';

const CREATE_ACTION = 'CREATE_CLIENT';

export const Client = () => {
    const clientList = useSelector((state) => state.client.clientList);
    const clientExpiration = useSelector((state) => state.client.clientExpiration);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const success = FindSuccess(CREATE_ACTION);
    const { open, close } = useModal();
    const classes = buttonsStyle();
    const canReadAuth = RolePermission([PermissionList.CLIENT_AUTHORIZATION_READ?.code, PermissionList.CLIENT_AUTHORIZATION_MANAGE?.code]);
    const tagList = useSelector((state) => state.tags.tagList);
    const fundingSourceList = useSelector((state) => state.fundingSource.fundingSourceList);
    const canReadPayers = canReadClientPayers();
    const canFilterByPayer = canFilterClientsByPayer();
    const payerOptions = canFilterByPayer ? fundingSourceList?.funders?.map((p) => ({ id: p.id, name: p.name })) || [] : [];

    /**
     * Everything that narrows this grid: the Status and Payer columns, the Tags column control, the
     * auth-expiry pills and the top-bar search.
     *
     * `ClientGetDTO` also accepts `gender`, but the Gender column is `disabled: true` and there is no
     * filter popover on this page, so nothing sets it — a chip for it could never appear.
     */
    const filtersList = [
        { name: 'Status', key: 'statuses', options: clientStatusFilterList, defaultValue: defaultStatuses },
        canFilterByPayer && { name: 'Payer', key: 'payer', options: payerOptions, loading: !fundingSourceList?.funders },
        { name: 'Tag', key: 'tags', options: tagList, loading: !tagList?.length },
        canReadAuth && {
            name: 'Auth',
            key: 'authExpiration',
            render: (v) => (v === clientExpirationEnums.AUTH_EXPIRED ? 'Expired' : 'Expiring Soon'),
        },
        { name: 'Search', key: 'searchValue', companions: ['searchKey'] },
    ].filter(Boolean);

    const renderParams = () => {
        const filteredInfo = {
            ...info,
        };
        filteredInfo.skip = info?.skip ? info?.skip : 0;
        filteredInfo.limit = 50;
        if (!info?.statuses) {
            filteredInfo.statuses = defaultStatuses;
        } else if (info?.statuses?.length) {
            filteredInfo.statuses = info?.statuses;
        } else {
            delete filteredInfo.statuses;
        }
        delete filteredInfo.page;
        delete filteredInfo.activeTab;
        delete filteredInfo.name;
        // A saved/bookmarked auth filter from before the permission was removed — the API answers 403 to it
        if (!canReadAuth) delete filteredInfo.authExpiration;
        // Same for a saved payer filter / payer sort
        if (!canReadPayers) {
            delete filteredInfo.payer;
            if (filteredInfo.sortType === 'payer') {
                delete filteredInfo.sortType;
                delete filteredInfo.orderType;
            }
        }
        return filteredInfo;
    };

    const getClients = (load) => {
        dispatch(clientActions.getClients(renderParams(), load));
        // dispatch(clientActions.getClientExpiration(renderParams()));
    };

    useEffect(() => {
        dispatch(tagActions.getTags({ module: tagEnums.CLIENT }));
        if (canFilterByPayer) dispatch(fundingSourceActions.getFundingSource({ sortType: 'name', orderType: 'ASC' }, 'noLoad'));
        dispatch(clientActions.getClientExpiration(renderParams()));

    }, []);

    useEffect(() => {
        getClients();
    }, [info]);

    useEffect(() => {
        if (!!success?.length) {
            close();
            getClients('noLoad');
            dispatch(httpRequestsOnSuccessActions.removeSuccess(CREATE_ACTION));
        }
    }, [success]);

    const handleAuthFilter = (type) => {
        const params = {
            ...info
        }

        if(params?.authExpiration === type){
            params.authExpiration = null;
        }else{
            params.authExpiration  = type;
        }

        SavePage(navigate, info, params);
    };

    const handleNavigate = (id, item) => {
        if (RolePermission([PermissionList.CLIENT_READ?.code])) {
            navigate(`/client/${id}`);
        }
    };

    const handleAction = async (item, name) => {
        if (name === 'resendInvite') {
            open(<ResendIntakeForm client={item} />);
        }
        if (name === 'completeInvite') {
            open(<ClientConfirmForm client={item} afterSuccess={() => getClients('noLoad')} />);
        }
    };

    return (
        <div>
            <div className="flex-end" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
                <div className="flex-align-center" style={{ gap: 16 }}>
                    {canReadAuth && clientExpiration?.authExpired > 0 && (
                        <button
                            type="button"
                            className="auth-filter-pill auth-filter-pill--expired"
                            onClick={() => handleAuthFilter(clientExpirationEnums.AUTH_EXPIRED)}
                            style={{
                                border: `1px solid ${info?.authExpiration === clientExpirationEnums.AUTH_EXPIRED ? '#FEE4E2' : '#FECDCA'}`,
                                background:info?.authExpiration === clientExpirationEnums.AUTH_EXPIRED  ? '#FEF3F2' : '#FFF',
                                color: '#B42318',
                            }}
                        >
                            <img style={{ width: 15, height: 15 }} src={Images.alertError} alt="error" />
                            Expired
                            <span style={{ color: '#F04438' }}>({clientExpiration?.authExpired})</span>
                        </button> 
                    )}
                    {canReadAuth && clientExpiration?.expiringSoon > 0 && (
                        <button
                            type="button"
                            className="auth-filter-pill auth-filter-pill--expiring"
                            onClick={() => handleAuthFilter(clientExpirationEnums.EXPIRING_SOON)}
                            style={{
                                border: `1px solid #F4D0B1`,
                                background: info?.authExpiration === clientExpirationEnums.EXPIRING_SOON  ? '#FFFAEB' : '#FFF',
                                color: '#B54708',
                            }}
                        >
                            <img style={{ width: 15, height: 15 }} src={Images.alertWarning} alt="error" />
                            Expiring Soon
                            <span style={{ color: '#F04438' }}>({clientExpiration?.expiringSoon})</span>
                        </button>
                    )}
                </div>
                <div className="flex-align-center" style={{ gap: 16 }}>
                    {canReadAuth && (
                        <SimpleTooltip title="Authorization Utilization" placement="top">
                            <button
                                type="button"
                                className="auth-utilization-btn"
                                onClick={() => navigate('/client/authorization-utilization')}
                            >
                                <img src={Images.authorizationUtilization} alt="authorizationUtilization" />
                            </button>
                        </SimpleTooltip>
                    )}
                    <DownloadCsv type={'client'} />
                    {RolePermission([PermissionList.CLIENT_CREATE?.code]) && (
                        <ManageActions
                            btnClass={`${classes.addButtonStyle} flex-align-center`}
                            btnIcon={Images.addCircle}
                            btnTitle={'Add Client'}
                            styleBodyItems={{ padding: 0 }}
                            renderBody={(closePopover) => (
                                <div className="flex" style={{ flexDirection: 'column' }}>
                                    <button
                                        className="header-add-goals-items-btn"
                                        onClick={() => {
                                            open(<CreateClient />);
                                            closePopover?.();
                                        }}
                                    >
                                        Add Client
                                    </button>
                                    <button
                                        className="header-add-goals-items-btn"
                                        onClick={() => {
                                            open(<ClientIntakeForm afterSuccess={() => getClients('noLoad')} />);
                                            closePopover?.();
                                        }}
                                    >
                                        Send Intake Form
                                    </button>
                                </div>
                            )}
                        />
                    )}
                </div>
            </div>

            <SelectedFilters filtersList={filtersList} doNotCrop />

            <FullTable
                head={clientHead(payerOptions)}
                body={clientBody(renderParams(), handleAction)}
                loadingType={ACTION_TYPE}
                list={sortListByKey(clientList?.clients, 'code', info)}
                listCount={clientList?.count}
                handleClick={(id, item) => handleNavigate(id, item)}
                notYet={clientNotYet}
                detailPage={(item) => {
                    if (RolePermission([PermissionList.CLIENT_READ?.code])) {
                        return `/client/${item?.id}`;
                    }
                }}
                margin={'12px 0 0 0'}
            />
        </div>
    );
};
